import { useMemo } from 'react';
import { Link, useLoaderData, useLocation } from 'react-router';
import { useAtom } from 'jotai';
import type { loader as productLoader } from '~/routes/_app.products.$';

import { CategoryTilesGrid } from '@ui-rocco/pages/category-page/CategoryTilesGrid';
import { CategoryNavigationBar } from '@ui-rocco/pages/category-page/CategoryNavigationBar';
import { categoryToggleAtom } from '@rocco/states/categoryToggleAtom';

/**
 * Category navigation and tiles section for /products pages.
 * On root /products: shows Interior/Building toggle with vertical cards.
 * On category pages: shows subcategories directly with horizontal tiles.
 */
export const CategoryPageProducts = () => {
    const { productSection, filterStates, directoryPageMeta } =
        useLoaderData<typeof productLoader>();
    const location = useLocation();

    // Detect if on root /products or first-level category page
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isProductsRoot =
        pathSegments.length === 1 && pathSegments[0] === 'products';

    const [activeToggle, setActiveToggle] = useAtom(categoryToggleAtom);

    // On root: use filtered categories by toggle; On first-level: use all categories
    const displayCategories = useMemo(() => {
        if (isProductsRoot) {
            return activeToggle === 'interior'
                ? (productSection.interiorCategories ?? [])
                : (productSection.buildingCategories ?? []);
        }
        // First-level category page: show subcategories directly
        return productSection.categories ?? [];
    }, [
        isProductsRoot,
        activeToggle,
        productSection.interiorCategories,
        productSection.buildingCategories,
        productSection.categories,
    ]);

    // Pills use same logic
    const pillCategories = useMemo(() => {
        const cats = isProductsRoot
            ? activeToggle === 'interior'
                ? productSection.interiorCategories
                : productSection.buildingCategories
            : productSection.categories;

        return (
            cats?.map((cat) => ({
                id: cat.id,
                title: cat.title,
                link: cat.link,
            })) ?? []
        );
    }, [
        isProductsRoot,
        activeToggle,
        productSection.interiorCategories,
        productSection.buildingCategories,
        productSection.categories,
    ]);

    // Calculate count for the header based on toggle selection
    const displayCount = useMemo(() => {
        if (isProductsRoot) {
            // Sum item counts from filtered categories
            const cats =
                activeToggle === 'interior'
                    ? productSection.interiorCategories
                    : productSection.buildingCategories;
            return (
                cats?.reduce((sum, cat) => sum + (cat.itemsCount ?? 0), 0) ?? 0
            );
        }
        // First-level: use total count from filterStates
        return filterStates.totalCount;
    }, [
        isProductsRoot,
        activeToggle,
        productSection.interiorCategories,
        productSection.buildingCategories,
        filterStates.totalCount,
    ]);

    // Get category title for first-level pages (used by CategoryTilesGrid)
    const categoryTitle = !isProductsRoot
        ? directoryPageMeta.directoryHeaderTitle
        : undefined;

    // Transform breadcrumbs: rename "Products" → "Product library" to match browse mode
    const transformedBreadcrumbs = useMemo(
        () =>
            directoryPageMeta.breadcrumbs.map(({ href, label }) => ({
                href,
                label: label === 'Products' ? 'Product library' : label,
            })),
        [directoryPageMeta.breadcrumbs],
    );

    // Compute parent category info from breadcrumbs for the back button.
    // breadcrumbs order: [Root("Products"), ..., Parent, Current]
    const parentInfo = useMemo(() => {
        const { breadcrumbs } = directoryPageMeta;

        if (breadcrumbs.length < 2) {
            return { name: 'All Product Categories', link: '/products' };
        }

        const parent = breadcrumbs[breadcrumbs.length - 2];
        if (!parent) {
            return { name: 'All Product Categories', link: '/products' };
        }

        // First-level categories: parent is root /products
        if (parent.href === '/products') {
            return { name: 'All Product Categories', link: '/products' };
        }

        // Special case: direct parent is building-hardware → navigate to root instead.
        // CategoryNavigationBar's handleBackClick will set the building toggle when
        // navigating to /products from a building-hardware path.
        if (parent.href === '/products/building-hardware') {
            return { name: 'All Product Categories', link: '/products' };
        }

        // Normal case: use actual parent breadcrumb
        return { name: parent.label, link: parent.href };
    }, [directoryPageMeta]);

    return (
        <section className="mb-20 md:mb-45">
            {/* Navigation bar with toggle, search, and pills */}
            <CategoryNavigationBar
                activeToggle={activeToggle}
                onToggleChange={setActiveToggle}
                showToggle={isProductsRoot}
                categories={pillCategories}
                parentCategoryName={parentInfo.name}
                parentCategoryLink={parentInfo.link}
            />

            <div className="md:my-4">
                {/* Breadcrumbs - matching ProductsFilterBar style */}
                <div className="px-4 py-12 md:px-12">
                    <hr className="m-0 h-0 border-t border-[#232323] opacity-20" />
                    {transformedBreadcrumbs.length > 0 && (
                        <nav aria-label="Breadcrumb" className="m-0 pt-5">
                            <ol className="flex flex-wrap items-center">
                                {transformedBreadcrumbs.map((crumb, index) => (
                                    <li
                                        key={crumb.href}
                                        className="inline-flex items-center text-5.5 leading-normal font-medium tracking-[-0.22px] text-[#323232]"
                                    >
                                        {index > 0 && (
                                            <span
                                                className="mx-2"
                                                aria-hidden="true"
                                            >
                                                /
                                            </span>
                                        )}
                                        <Link
                                            to={crumb.href}
                                            state={{
                                                restoreScroll: true,
                                                scroll: false,
                                            }}
                                        >
                                            {crumb.label}
                                        </Link>
                                    </li>
                                ))}
                            </ol>
                        </nav>
                    )}
                </div>
            </div>

            {/* Grid layout - filtered by toggle on root, subcategories on first-level */}
            <CategoryTilesGrid
                categories={displayCategories}
                variant={isProductsRoot ? 'vertical' : 'horizontal'}
                categoryName={categoryTitle}
                totalCount={displayCount}
            />
        </section>
    );
};
